"use client";

import React, { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import styles from "@/components/common/ResultsSliderBase/Results.module.scss";
import { useResultsContext } from "@/components/common/ResultsSliderBase/ResultsContext";
import Slider, { type Settings as SlickSettings } from "@/lib/react-slick";
import { InteractionType } from "@/types/ga4";
import { GA4 } from "@/utils/ga4Utils";
import { cn, getArrowDirectionClicked } from "@/utils/general";

export type ResponsiveGap = [number, number?, number?, number?];

// Get the current slidesToShow based on slides to show setting prop
function useCurrentSlidesToShow(settings?: SlickSettings): number {
  const [slidesToShow, setSlidesToShow] = useState(settings?.slidesToShow ?? 1);

  useEffect(() => {
    const updateSlidesToShow = () => {
      const width = window.innerWidth;
      let current = settings?.slidesToShow ?? 1;

      // Check responsive breakpoints (sorted by breakpoint descending)
      if (settings?.responsive) {
        const sorted = [...settings.responsive].sort((a, b) => b.breakpoint - a.breakpoint);
        for (const resp of sorted) {
          if (width <= resp.breakpoint && resp.settings !== "unslick") {
            current = resp.settings.slidesToShow ?? current;
          }
        }
      }

      setSlidesToShow(current);
    };

    updateSlidesToShow();
    window.addEventListener("resize", updateSlidesToShow);
    return () => window.removeEventListener("resize", updateSlidesToShow);
  }, [settings]);

  return slidesToShow;
}

type ResultsSliderProps = {
  children: ReactNode;
  className?: string;
  settings?: SlickSettings;
  gap?: number | ResponsiveGap;
  trackObject?: {
    module_location?: string;
  };
};

export function ResultsResultsSlider({
  children,
  className,
  settings,
  gap,
  trackObject = {},
}: ResultsSliderProps) {
  const {
    activeIndex,
    setActiveIndex,
    autoplay,
    setAutoplay,
    autoplaySpeed,
    setAutoplaySpeed,
    thumbnailSliderRef,
    setResultSliderRef,
    resultSliderRef,
    slidesCount,
  } = useResultsContext();

  // Get current slidesToShow based on slides to show setting prop
  const currentSlidesToShow = useCurrentSlidesToShow(settings);
  const shouldAutoplay = autoplay && slidesCount > currentSlidesToShow;

  // Create gap styles from responsive gap variables
  const gapStyles = useMemo((): CSSProperties => {
    if (!gap) return {};

    if (typeof gap === "number") {
      return { "--gap-default": `${gap}px` } as CSSProperties;
    }

    const [defaultGap, md, lg, xl] = gap;
    const vars: Record<string, string> = {};
    if (defaultGap !== undefined) vars["--gap-default"] = `${defaultGap}px`;
    if (md !== undefined) vars["--gap-md"] = `${md}px`;
    if (lg !== undefined) vars["--gap-lg"] = `${lg}px`;
    if (xl !== undefined) vars["--gap-xl"] = `${xl}px`;

    return vars as CSSProperties;
  }, [gap]);

  const track = (index: number, arrowDirection: InteractionType) => {
    GA4.onCarouselClicked({
      module_location: trackObject?.module_location || "mid_fold",
      module_name: "Before & After",
      slide_number: index + 1,
      arrow: arrowDirection,
      content_type: "image",
    });
  };

  useEffect(() => {
    if (settings?.autoplay === false) {
      setAutoplay(false);
    }
    if (settings?.autoplaySpeed) {
      setAutoplaySpeed(settings.autoplaySpeed);
    }
  }, [settings, setAutoplay, setAutoplaySpeed]);

  useEffect(() => {
    if (!shouldAutoplay && resultSliderRef?.slickPause) {
      resultSliderRef.slickPause();
    } else if (shouldAutoplay && resultSliderRef?.slickPlay) {
      resultSliderRef.slickPlay();
    }
  }, [shouldAutoplay, resultSliderRef]);

  const sliderSettings: SlickSettings & { ref?: (slider: Slider | null) => void } = {
    /*
     * Do not use asNavFor. This slider is infinite with 1 slide in view; the
     * thumbnail strip is not. Two-way asNavFor leaves the track between snaps
     * after a swipe or mouse drag (NT2-32999).
     */
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    arrows: false,
    waitForAnimate: true,
    autoplaySpeed: autoplaySpeed,
    autoplay: shouldAutoplay,
    ref: (slider: Slider | null) => {
      setResultSliderRef(slider);
    },
    beforeChange: (oldIndex, newIndex) => {
      setActiveIndex(newIndex);
      thumbnailSliderRef?.slickGoTo?.(newIndex);
      if (!autoplay) {
        const direction = getArrowDirectionClicked(oldIndex, newIndex, slidesCount);
        if (direction === "left" || direction === "right") {
          track(newIndex, direction);
        }
      }
    },
    swipeEvent(direction) {
      if (autoplay) {
        setAutoplay(false);
        track(activeIndex + 1, undefined);
      }
      track(activeIndex + 1, direction === "left" ? "right" : "left");
    },
    ...settings,
  };

  return (
    <div className="flex">
      <div className={cn(styles.sliderWrapper, "relative w-full", className)} style={gapStyles}>
        <Slider {...sliderSettings}>{children}</Slider>
      </div>
    </div>
  );
}
